/**
 * Day/night ambiance — sole owner of time-of-day logic.
 *
 * Seeds sun position from browser local time once, then advances on a local
 * timer at real-time rate (no re-reading the clock, no network). Future
 * realism (seasonal drift, weather) extends this module only.
 */

import { sunPositionAt } from "@web/features/globe/solar.ts";

export type AmbianceGlyph = "sun" | "moon";

export interface SunPosition {
  lng: number;
  lat: number;
}

export interface AmbianceClock {
  /** Simulated unix ms — advanced by real elapsed time from seed. */
  simTimeMs: number;
  sun: SunPosition;
  glyph: AmbianceGlyph;
}

/** Soft blue-white atmosphere — sits with day/night textures (no purple haze). */
export const ATMOSPHERE_COLOR = "#9ec9ff";
export const ATMOSPHERE_ALTITUDE = 0.2;

/** Seed clock from browser local time (once). */
export function seedAmbianceClock(now: Date = new Date()): AmbianceClock {
  const simTimeMs = now.getTime();
  return {
    simTimeMs,
    sun: sunPositionAt(simTimeMs),
    glyph: glyphForHour(now.getHours()),
  };
}

/**
 * Advance the simulated clock by real elapsed ms (1:1). Call from rAF/interval;
 * do not re-read `Date` for the sun.
 */
export function advanceAmbianceClock(
  clock: AmbianceClock,
  deltaMs: number,
): AmbianceClock {
  const simTimeMs = clock.simTimeMs + Math.max(0, deltaMs);
  const sun = sunPositionAt(simTimeMs);
  const hour = new Date(simTimeMs).getHours();
  return { simTimeMs, sun, glyph: glyphForHour(hour) };
}

function glyphForHour(hour: number): AmbianceGlyph {
  const h = ((hour % 24) + 24) % 24;
  return h >= 7 && h < 19 ? "sun" : "moon";
}

/** Day/night blend shader — adapted from globe.gl day-night-cycle example. */
export const DAY_NIGHT_SHADER = {
  vertexShader: `
    varying vec3 vNormal;
    varying vec2 vUv;
    void main() {
      vNormal = normalize(normalMatrix * normal);
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: `
    #define PI 3.141592653589793
    uniform sampler2D dayTexture;
    uniform sampler2D nightTexture;
    uniform vec2 sunPosition;
    uniform vec2 globeRotation;
    varying vec3 vNormal;
    varying vec2 vUv;

    float toRad(in float a) {
      return a * PI / 180.0;
    }

    vec3 Polar2Cartesian(in vec2 c) {
      float theta = toRad(90.0 - c.x);
      float phi = toRad(90.0 - c.y);
      return vec3(
        sin(phi) * cos(theta),
        cos(phi),
        sin(phi) * sin(theta)
      );
    }

    void main() {
      float invLon = toRad(globeRotation.x);
      float invLat = -toRad(globeRotation.y);
      mat3 rotX = mat3(
        1, 0, 0,
        0, cos(invLat), -sin(invLat),
        0, sin(invLat), cos(invLat)
      );
      mat3 rotY = mat3(
        cos(invLon), 0, sin(invLon),
        0, 1, 0,
        -sin(invLon), 0, cos(invLon)
      );
      vec3 rotatedSunDirection = rotX * rotY * Polar2Cartesian(sunPosition);
      float intensity = dot(normalize(vNormal), normalize(rotatedSunDirection));
      vec4 dayColor = texture2D(dayTexture, vUv);
      vec4 nightColor = texture2D(nightTexture, vUv);
      float blendFactor = smoothstep(-0.1, 0.1, intensity);
      gl_FragColor = mix(nightColor, dayColor, blendFactor);
    }
  `,
} as const;
